import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { playInsertCoinSound, playButtonSound } from '../lib/sounds';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      playInsertCoinSound();
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background arcade image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20 mix-blend-screen"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1563277267-0e9f2dabb9ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxyZXRybyUyMGFyY2FkZSUyMG1hY2hpbmV8ZW58MHx8fHwxNzc2NDMxOTY5fDA&ixlib=rb-4.1.0&q=85)' }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-7xl sm:text-8xl uppercase tracking-widest text-[#E61C24] drop-shadow-[0_0_15px_rgba(230,28,36,0.8)]">
            90"
          </h1>
          <p className="font-heading text-7xl sm:text-8xl text-[#E61C24] tracking-widest drop-shadow-[0_0_15px_rgba(230,28,36,0.8)]">
            TO WIN
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-[#121214] border-2 border-[#E61C24]/50 shadow-[inset_0_0_20px_rgba(230,28,36,0.15)] p-6">
          <div className="flex items-center gap-2 mb-6">
            <Zap className="text-[#FFF200] drop-shadow-[0_0_8px_rgba(255,242,0,0.8)]" size={24} />
            <h2 className="font-heading text-2xl uppercase tracking-wider text-white">Player Login</h2>
          </div>

          {error && (
            <div data-testid="login-error" className="bg-[#E61C24]/20 border border-[#E61C24] text-[#FF333B] font-mono text-sm p-3 mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-mono text-[#00AEEF] text-sm mb-1 uppercase">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-black border-2 border-[#00AEEF]/50 text-white font-mono text-lg p-3 w-full focus:border-[#00AEEF] focus:ring-2 focus:ring-[#00AEEF]/40 focus:outline-none transition-all placeholder:text-gray-600 rounded-none"
                placeholder="your@email.com"
                required
              />
            </div>
            <div>
              <label className="block font-mono text-[#00AEEF] text-sm mb-1 uppercase">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="bg-black border-2 border-[#00AEEF]/50 text-white font-mono text-lg p-3 w-full focus:border-[#00AEEF] focus:ring-2 focus:ring-[#00AEEF]/40 focus:outline-none transition-all placeholder:text-gray-600 rounded-none"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="bg-[#E61C24] hover:bg-[#FF333B] text-white font-heading text-xl uppercase tracking-widest py-4 px-8 border-b-4 border-red-900 active:border-b-0 active:translate-y-1 transition-all shadow-[0_0_15px_rgba(230,28,36,0.6)] hover:shadow-[0_0_25px_rgba(255,51,59,0.9)] w-full text-center disabled:opacity-50"
            >
              {loading ? 'LOADING...' : 'START GAME'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/register" data-testid="register-link" className="font-mono text-[#00AEEF] hover:text-[#33C3FF] text-sm underline transition-colors">
              NEW PLAYER? JOIN NOW
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
